// Data layer of the public live schedule: one fetch, the Berlin → local clock, and the
// per-date overrides. Ported from the Library's dmc_live_section.liquid (the most complete
// copy of these rules), minus everything that needs a member: bookings, join links, upgrade.

// PUBLIC endpoint ONLY. /get-all-data carries every slot's Zoom link and must never be
// called from a page anyone can open; /get-public-data is the same shape with the links
// stripped, and nothing here reads anything else.
const API_SCHEDULE_URL =
  'https://scheduleclassesmanager-356018239005.europe-west1.run.app/get-public-data';

export const DEFAULT_INSTRUCTOR_AVATAR =
  'https://s3.amazonaws.com/kajabi-storefronts-production/static_assets/default_avatar.jpg';

export const DAY_NAMES = [
  'Sundays',
  'Mondays',
  'Tuesdays',
  'Wednesdays',
  'Thursdays',
  'Fridays',
  'Saturdays',
];

// One request per page, shared by every mount, the popup and the detail dialog. It
// resolves to the RAW payload: buildSchedule() runs on every render, because which
// occurrence an override lands on (and so whether a card is cancelled this week) moves
// with the clock on a page left open.
let schedulePromise = null;

export function loadSchedule() {
  if (schedulePromise) return schedulePromise;
  schedulePromise = fetch(API_SCHEDULE_URL)
    .then((r) => {
      if (!r.ok) throw new Error('Schedule API HTTP ' + r.status);
      return r.json();
    })
    .catch((err) => {
      // Forget the failure so the next mount (or a refresh) tries again.
      schedulePromise = null;
      throw err;
    });
  return schedulePromise;
}

export function buildSchedule(raw) {
  const instructors = (raw.instructors || []).map((inst) => ({
    id: inst.id,
    firstName: (inst.information && inst.information.firstName) || '',
    image_url: inst.image_url || DEFAULT_INSTRUCTOR_AVATAR,
  }));
  const classMap = new Map((raw.classes || []).map((c) => [c.id, c]));

  const classes = (raw.slots || []).map((slot) => {
    const info = classMap.get(slot.classId) || {};
    return {
      id: slot.id,
      classId: slot.classId || null,
      className: info.className || slot.className || '',
      category: info.category || '',
      duration: slot.duration,
      idInstructor: slot.instructorId,
      dayLocal: slot.dayLocal,
      timeLocal: slotTime(slot.scheduleKey || slot.id),
      level: info.level || '',
      description: info.description || '',
      // `startTime` holds "HH:MM" on today's slots; only a real "YYYY-MM-DD" means "this
      // slot begins its run on that date", which is what the occurrence math reads it as.
      starting: /^\d{4}-\d{2}-\d{2}/.test(slot.startTime || '')
        ? slot.startTime
        : null,
      active: slot.active,
      resumesOn: slot.resumesOn || null,
    };
  });

  return {
    instructors,
    classes: applyDateOverrides(classes, raw.overrides, instructors),
  };
}

// "mon1630" / "fri0800" → "16:30" / "08:00".
function slotTime(id) {
  const t = String(id).replace(/^\D+/g, '');
  return t.length === 4 ? t.substring(0, 2) + ':' + t.substring(2) : '00:00';
}

/* One-date exceptions from the admin Live Schedule (liveSlotOverrides, served stripped by
   get-public-data as { slotId, date, cancelled, instructorId }; `date` = the occurrence's
   BERLIN date). The grid shows ONE occurrence per weekly slot, so an override only matters
   when it lands on that occurrence:
     - cancelled    -> the card becomes a suspension for this week: active:false + resumesOn =
                       the first later week that is NOT cancelled, so it reads "Back on <date>".
     - instructorId -> the substitute is shown for this week.
   A weekly suspension (slot.active === false) wins over any override: it already says when
   the class is back. A missing `overrides` leaves the weekly schedule untouched. */
function applyDateOverrides(classes, overrides, instructors) {
  if (!Array.isArray(overrides) || overrides.length === 0) return classes;
  const byKey = new Map();
  overrides.forEach((o) => {
    if (o && o.slotId && o.date) byKey.set(`${o.slotId}_${o.date}`, o);
  });
  const isCancelled = (slotId, date) =>
    !!(byKey.get(`${slotId}_${date}`) || {}).cancelled;

  return classes.map((cls) => {
    if (cls.active === false || !cls.timeLocal) return cls;
    // The occurrence the card is about: the one on air right now (started less than its
    // duration ago) or else the next one. Without the look-back, a cancelled class would
    // flip to next week the minute it was due to start and show "Live Now".
    const lookBackMs = Math.max(Number(cls.duration) || 0, 30) * 60000;
    const occIso = getNextOccurrence(
      cls.dayLocal,
      cls.timeLocal,
      cls.starting,
      new Date(Date.now() - lookBackMs),
    );
    const date = berlinDateOf(occIso);
    const ov = byKey.get(`${cls.id}_${date}`);
    if (!ov) return cls;

    if (ov.cancelled) {
      let back = date;
      for (let i = 0; i < 12; i++) {
        back = addDaysToIsoDate(back, 7);
        if (!isCancelled(cls.id, back)) break;
      }
      return { ...cls, active: false, resumesOn: back, cancelledOn: date };
    }

    if (
      ov.instructorId &&
      ov.instructorId !== cls.idInstructor &&
      instructors.some((i) => i.id === ov.instructorId)
    ) {
      return {
        ...cls,
        idInstructor: ov.instructorId,
        weeklyInstructorId: cls.idInstructor,
      };
    }
    return cls;
  });
}

// ─── Clock ───────────────────────────────────────────────────────────────────────────

// "YYYY-MM-DD" of an instant on the Berlin calendar (en-CA formats as ISO date).
function berlinDateOf(iso) {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: 'Europe/Berlin',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date(iso));
}

// Calendar arithmetic on a plain "YYYY-MM-DD" (UTC, so no DST hour can shift the day).
function addDaysToIsoDate(isoDate, days) {
  const [y, m, d] = isoDate.split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d + days)).toISOString().slice(0, 10);
}

const berlinFormatter = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Europe/Berlin',
  year: 'numeric',
  month: 'numeric',
  day: 'numeric',
  hour: 'numeric',
  minute: 'numeric',
  hour12: false,
});

function berlinParts(date) {
  const p = {};
  berlinFormatter
    .formatToParts(date)
    .forEach((part) => (p[part.type] = part.value));
  return {
    year: parseInt(p.year),
    month: parseInt(p.month) - 1,
    day: parseInt(p.day),
    hour: parseInt(p.hour),
    min: parseInt(p.minute),
    fullDate: new Date(p.year, p.month - 1, p.day),
  };
}

// The next occurrence (ISO, UTC) of a weekly Berlin slot. `fromDate` (optional) = the
// instant to look forward from; defaults to now.
export function getNextOccurrence(
  dayLocal,
  timeLocal,
  starting = null,
  fromDate = null,
) {
  if (!timeLocal) return new Date().toISOString();
  const [hours, minutes] = timeLocal.split(':').map(Number);
  const nowBerlin = berlinParts(fromDate || new Date());

  let refDate = nowBerlin;
  let isStartingDateInFuture = false;
  if (starting) {
    const s = starting.split('-').map(Number);
    if (new Date(s[0], s[1] - 1, s[2]) > nowBerlin.fullDate) {
      refDate = { year: s[0], month: s[1] - 1, day: s[2] };
      isStartingDateInFuture = true;
    }
  }

  const refDayOfWeek = new Date(
    refDate.year,
    refDate.month,
    refDate.day,
  ).getDay();
  let daysUntil = (dayLocal - refDayOfWeek + 7) % 7;
  if (
    daysUntil === 0 &&
    !isStartingDateInFuture &&
    refDate.hour * 60 + refDate.min >= hours * 60 + minutes
  ) {
    daysUntil = 7;
  }

  const target = new Date(refDate.year, refDate.month, refDate.day + daysUntil);
  const berlinISO =
    `${target.getFullYear()}-${String(target.getMonth() + 1).padStart(2, '0')}-` +
    `${String(target.getDate()).padStart(2, '0')}T${String(hours).padStart(2, '0')}:` +
    `${String(minutes).padStart(2, '0')}:00`;
  const offset = new Intl.DateTimeFormat('en-US', {
    timeZone: 'Europe/Berlin',
    timeZoneName: 'longOffset',
  })
    .formatToParts(new Date(berlinISO))
    .find((p) => p.type === 'timeZoneName').value;
  return new Date(berlinISO + offset.replace('GMT', '')).toISOString();
}

// The visitor's own day (0 = Sunday) and "HH:MM" for the next occurrence of a slot.
export function localTimeOf(cls) {
  const iso = getNextOccurrence(cls.dayLocal, cls.timeLocal, cls.starting);
  const local = new Date(iso);
  return {
    day: local.getDay(),
    hour:
      String(local.getHours()).padStart(2, '0') +
      ':' +
      String(local.getMinutes()).padStart(2, '0'),
    isoFull: iso,
  };
}

function nowBerlinWall() {
  return new Date(
    new Date().toLocaleString('en-US', { timeZone: 'Europe/Berlin' }),
  );
}

function notStartedYet(cls, nowBerlin) {
  if (!cls.starting) return false;
  const s = cls.starting.split('-').map(Number);
  return nowBerlin < new Date(s[0], s[1] - 1, s[2], 0, 0, 0);
}

// Signed minutes between now and the slot on the Berlin weekly clock.
function weeklyDiff(cls, nowBerlin) {
  const [h, m] = cls.timeLocal.split(':').map(Number);
  const nowMinutes =
    nowBerlin.getDay() * 1440 +
    nowBerlin.getHours() * 60 +
    nowBerlin.getMinutes();
  return cls.dayLocal * 1440 + h * 60 + m - nowMinutes;
}

// On air: from 5 minutes before the start to 30 minutes after it (the Library's window).
export function isClassNow(cls) {
  const nowBerlin = nowBerlinWall();
  if (notStartedYet(cls, nowBerlin)) return false;
  let diff = -weeklyDiff(cls, nowBerlin);
  if (diff > 5000) diff -= 10080;
  if (diff < -5000) diff += 10080;
  return diff >= -5 && diff <= 30;
}

// Minutes until the class next starts, wrapping across the week end. `null` = not a
// candidate (a slot that has not begun its run yet has no occurrence this week).
function minutesUntilStart(cls) {
  const nowBerlin = nowBerlinWall();
  if (notStartedYet(cls, nowBerlin)) return null;
  let diff = weeklyDiff(cls, nowBerlin);
  if (diff < 0) diff += 10080;
  return diff;
}

/* The id of the single class that wears the "Next" pill: the soonest one still ahead — or
   null while ANY class is on air, because Live Now owns the moment and the two pills are
   the same red. Suspended slots never qualify. */
export function findNextClassId(list) {
  if (list.some((cls) => cls.active !== false && isClassNow(cls))) return null;
  let bestId = null;
  let bestDiff = Infinity;
  list.forEach((cls) => {
    if (cls.active === false) return;
    const diff = minutesUntilStart(cls);
    if (diff === null || diff >= bestDiff) return;
    bestDiff = diff;
    bestId = cls.id;
  });
  return bestId;
}

// The Americas read the week from Sunday, everyone else from Monday.
export function getWeekStartIndex() {
  const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || '';
  return tz.startsWith('America/') ? 0 : 1;
}

export function timezoneLabel() {
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    return tz ? tz.split('/').pop().replace(/_/g, ' ') : '';
  } catch {
    return '';
  }
}

export function formatTo12h(timeStr) {
  if (!timeStr) return '';
  let [hours, minutes] = timeStr.split(':').map(Number);
  const ampm = hours >= 12 ? 'pm' : 'am';
  hours = hours % 12 || 12;
  return `${hours}:${String(minutes).padStart(2, '0')}${ampm}`;
}

// A suspended slot may carry `resumesOn` ("YYYY-MM-DD"). Built from the Y/M/D parts instead
// of new Date(string), which parses as UTC midnight and shows the PREVIOUS day west of
// Greenwich. Returns '' when absent or malformed, and callers then show no date at all.
export function formatResumeDate(resumesOn, style) {
  const m = String(resumesOn || '').match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return '';
  const [y, mo, day] = [Number(m[1]), Number(m[2]), Number(m[3])];
  const d = new Date(y, mo - 1, day);
  // Round-trip check: `new Date(2026, 12, 99)` silently rolls over to a wrong date.
  if (
    isNaN(d.getTime()) ||
    d.getFullYear() !== y ||
    d.getMonth() !== mo - 1 ||
    d.getDate() !== day
  ) {
    return '';
  }
  return style === 'long'
    ? d
        .toLocaleDateString('en-US', {
          weekday: 'long',
          month: 'long',
          day: 'numeric',
        })
        .replace(',', '')
    : d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
}

// [fill, textOnFill, outlineInk]. The 3rd value is for the OUTLINED pill on a suspended
// card: the fills are far too light to read as text on the near-white card, so these are
// the same hues darkened to >= 4.5:1.
export function tagColors(category) {
  const colors = {
    'ballet class': ['#4384db', '#ffffff', '#2973d6'],
    workout: ['#edff5d', '#000000', '#6e7c00'],
    stretching: ['#ffa6dd', '#000000', '#de0089'],
  };
  return (
    colors[String(category || '').toLowerCase()] || [
      '#cccccc',
      '#000000',
      '#5f5f5f',
    ]
  );
}

export function escapeHtml(str) {
  if (str == null) return '';
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;');
}
